import type { PackagingInformationEntry, ProductData } from "~/domains/product/types/ProductData";
import { getNextUID } from "~/common/utils/uid";
import type { AvailabilityInfo } from "~/domains/product/api/pna/AvailabilityInfo";
import type { PackCodeProvider } from "~/features/addToCart/hooks/useAddToCartNew";
import { selectPackageIsInStock } from "./pna/packagingInformationEntrySelectors";
import { splitBy, toMapFn } from "~/common/utils/reducers";
import { getPackageCodes } from "~/domains/product/utils/productDescriptorUtils";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import { calculateSalesCampaign4PDP } from "~/features/salesCampaign/utils/salesCampaignUtils";
import {
  getPackages,
  isDiscontinued,
  type PackagingWithStatus,
} from "~/features/productDetails/components/PhaseInOut/phaseInOutHelper";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";

export function getPackageCode(code: string) {
  return code.substring(code.length - 2);
}

export enum PSSizeMode {
  /**
   * best = largest
   */
  LARGEST_FIRST,
  /**
   * best = smallest
   */
  SMALLEST_FIRST,
}

/*
PSSizeMode.SMALLEST_FIRST means:
  1. Choose smallest available that divides by quantity
  2. Choose smallest available
  3. Choose smallest that divides by quantity
  4. Choose smallest

 */

/**
 * Select the package best suited for given quantity.
 * Order of selection:
 * On sale with stock
 * On sale without stock
 * Phased out in stock
 * Remaining in stock
 * Remaining out of stock
 *
 * @param quantity
 * @param candidatePackageCodes list of packages to choose from, or null if all are valid
 * @param product product descriptor
 * @param sizeMode which package is best
 * @param includeSoldOut
 */
export function defaultSelectBestPackage(
  quantity: number,
  candidatePackageCodes: string[] | null,
  product: ProductDescriptor,
  sizeMode: PSSizeMode,
  includeSoldOut = true,
) {
  const filterFn = candidatePackageCodes
    ? (p: PackagingWithStatus) => candidatePackageCodes.includes(p.packCode)
    : () => true;
  const packages = getPackages(product).filter(filterFn);

  const groupsOfPackages: PackagingWithStatus[][] = [];
  let remainingPackages = packages;

  // Let's consider sales campaign first
  const salesData = calculateSalesCampaign4PDP({ productDescriptor: product, includeSoldOut }).data;
  if (salesData?.hasSales) {
    const salePackageCodes = Object.keys(salesData.salePerPack);
    const [packagesOnSale, packagesNotOnSale] = splitBy(remainingPackages, (p) =>
      salePackageCodes.includes(p.packCode),
    );
    const [salePackagesWithoutStock, salePackagesWithStock] = splitBy(
      packagesOnSale,
      (p) => salesData.salePerPack[p.packCode].soldOut,
    );
    groupsOfPackages.push(salePackagesWithStock, salePackagesWithoutStock);
    remainingPackages = packagesNotOnSale;
  }

  // Let's consider phased out packages
  const [phasedOutPackages, otherPackages] = splitBy(remainingPackages, isDiscontinued);
  const [discontinuedWithStock, discontinuedWithoutStock] = splitBy(phasedOutPackages, selectPackageIsInStock);
  const [normalWithStock, normalWithoutStock] = splitBy(otherPackages, selectPackageIsInStock);
  groupsOfPackages.push(discontinuedWithStock);
  groupsOfPackages.push(normalWithStock);

  remainingPackages = [...discontinuedWithoutStock, ...normalWithoutStock];
  groupsOfPackages.push(remainingPackages);

  const candidates = firstNonEmptyGroup(groupsOfPackages).map((pack) => pack.packCode);
  return selectBestPackBasedOnQuantity(quantity, candidates, product, sizeMode);
}

/**
 * This function selects the best package based on the stock and quantity.
 * @param quantity
 * @param candidatePackageCodes
 * @param product
 * @param sizeMode
 */
export function selectBestPackBasedOnStock(
  quantity: number,
  candidatePackageCodes: string[] | null,
  product: ProductDescriptor,
  sizeMode: PSSizeMode,
) {
  const packages = getPackagingInformation(product, candidatePackageCodes);
  const groupsOfPackages = splitBy(packages, selectPackageIsInStock);

  const candidates = firstNonEmptyGroup(groupsOfPackages).map((pack) => pack.packCode);
  return selectBestPackBasedOnQuantity(quantity, candidates, product, sizeMode);
}

/**
 * This function selects the best package based on the quantity only.
 * @param quantity
 * @param candidatePackageCodes
 * @param product
 * @param sizeMode
 */
export function selectBestPackBasedOnQuantity(
  quantity: number,
  candidatePackageCodes: string[],
  product: ProductDescriptor,
  sizeMode: PSSizeMode,
) {
  const list = getPackagingInformation(product, candidatePackageCodes);
  if (sizeMode === PSSizeMode.LARGEST_FIRST) {
    list.reverse();
  }
  // 1. Choose the first divisible by quantity
  for (const packagingInfo of list) {
    if (Number.isInteger(quantity / packagingInfo.packSize)) {
      return packagingInfo;
    }
  }

  // 2. Return the first one
  return list[0];
}

export const selectPackageBasedOnQuery =
  (searchQuery: string) => (availability: AvailabilityInfo, product: ProductData) => {
    const query = searchQuery?.trim().toUpperCase();
    if (!query) {
      return undefined;
    }

    let packCode: string | null = null;
    const isQueryMatch = (text: string) => text.trim().toUpperCase() === query;

    // ************************************
    // 1. Try to match the query as alias containing dashes
    // ************************************
    packCode = Object.entries(product.packageAliases)
      .map(([packCode, aliases]) => {
        return [packCode, aliases.map((alias) => alias.replace(/-/g, ""))] as const;
      })
      .filter(([packCode, aliases]) => aliases.some(isQueryMatch))
      .map(([packCode, aliases]) => packCode)[0];

    // ************************************
    // 2. Try to match the query as alias - without dashes
    // ************************************
    if (!packCode) {
      packCode = Object.entries(product.packageAliases)
        .filter(([packCode, aliases]) => aliases.some(isQueryMatch))
        .map(([packCode, aliases]) => packCode)[0];
    }

    // ************************************
    // 3. Try to match the query as EAN
    // ************************************
    if (!packCode && product.fifteenDigit) {
      packCode = Object.entries(product.fifteenDigit)
        .filter(([packCode, fifteenDigitData]) => isQueryMatch(fifteenDigitData.schaefflerEan))
        .map(([packCode, fifteenDigitData]) => packCode)[0];
    }

    // ************************************
    // 4. Try to match the query as 15 digit material number
    // ************************************
    if (!packCode && query.length === 15 && product.sapMaterialNumber === query.substring(0, 13)) {
      packCode = query.substring(13);
    }

    // ************************************
    // 5. If query is 2 digits, then assume this is the packCode
    // ************************************
    if (!packCode && isPackCode.test(query)) {
      packCode = query;
    }

    if (!packCode) {
      return undefined;
    }

    return availability.packagingInformation.find((packaging) => packaging.packCode === packCode);
  };

const isPackCode = /^\d{2}$/;

export const DEFAULT_PACK_CODE_PROVIDER: PackCodeProvider = (product, quantity) => {
  return (
    selectBestPackBasedOnStock(quantity, getPackageCodes(product), product, PSSizeMode.SMALLEST_FIRST)?.packCode ??
    INVALID_PACKAGE_CODE
  );
};
export const queryBasedPackCodeProvider =
  (searchQuery: string): PackCodeProvider =>
  (product, quantity) => {
    const packaging = selectPackageBasedOnQuery(searchQuery)(product.availability!, product.base);
    return packaging?.packCode ?? DEFAULT_PACK_CODE_PROVIDER(product, quantity);
  };

export function createLinearProductCode(productCode: string) {
  return productCode + "$$" + getNextUID();
}

export function getHybrisProductCode(productCode: string) {
  return productCode.split("$$")[0];
}

function getPackagingInformation(product: ProductDescriptor, packageCodes: string[] | null) {
  if (!packageCodes) {
    return product.availability?.packagingInformation ?? EMPTY_LIST;
  }
  const packageMap = toMapFn(product.availability?.packagingInformation ?? [], (p) => p.packCode);
  return packageCodes.map((code) => packageMap[code]).filter(Boolean);
}

function firstNonEmptyGroup<T>(groups: T[][]): T[] {
  return groups.find((group) => group.length > 0) ?? [];
}

const EMPTY_LIST: PackagingInformationEntry[] = [];
